import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert, MenuItem, Select,
    InputLabel, FormControl, CircularProgress, Tooltip, Fade
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAllSubdepartment } from '../api/subDepartment-api';
import { getAllDocuments, createDocument, updateDocument, deleteDocument } from "../api/documentapi";
import { storage } from '../firebaseconfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const TypesManagement = ({filteredSubDepartments}) => {
    const [currentSubDepartment, setCurrentSubDepartment] = useState('');
    const [postState, setPostState] = useState({ id: null, name: '', description: '', subDepartmentId: currentSubDepartment, image: '', pdfUrl: '', author: '', createdAt: '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [pdfUploading, setPdfUploading] = useState(false);
    const handleCloseSnackbar = () => setSnackbarOpen(false);
    const { data: subDepartments = [], isLoading: isLoadingSubDepartments, error: subDepartmentError } = useQuery({
        queryKey: ['subDepartments'],
        queryFn: getAllSubdepartment, // Pass the function directly
    });
    const { data: documents = [], isLoading: isLoadingDocuments, error: documentsError } = useQuery({
        queryKey: ['documents', currentSubDepartment],
        queryFn: () => getAllDocuments(currentSubDepartment),
        enabled: !!currentSubDepartment,
    });

    const mutation = useMutation({
        mutationFn: (newPost) => {
            if (newPost.id) {
                return updateDocument(newPost.id, newPost);
            } else {
                return createDocument(newPost);
            }
        },
        onSuccess: () => {
            setSnackbarMessage('Document saved successfully.');
            setOpenDialog(false);
        },
        onError: () => {
            setSnackbarMessage('Failed to save document.');
        },
    });
    const handleOpenDialog = (post = { id: null, title: '', description: '', subDepartmentId: '', image: '', pdfUrl: '', author: '', createdAt: '' }) => {
        setPostState(post);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setPostState({ id: null, title: '', description: '', subDepartmentId: '', image: '', pdfUrl: '', author: '', createdAt: '' });
    };

    const handleSavePost = async () => {
        if (!postState.title || !postState.description || !currentSubDepartment) {
            setSnackbarMessage('Title, Description, and SubDepartment are required.');
            setSnackbarOpen(true);
            return;
        }

        setLoading(true);

        try {
            await mutation.mutateAsync(postState);
        } catch (error) {
            console.error('Error saving document:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = (file) => {
        if (!file) return;

        const storageRef = ref(storage, `images/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        setImageUploading(true);

        uploadTask.on(
            'state_changed',
            () => { },
            (error) => {
                console.error('Error uploading image:', error);
                setSnackbarMessage('Failed to upload image.');
                setSnackbarOpen(true);
                setImageUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setPostState((prevState) => ({ ...prevState, image: downloadURL }));
                    setImageUploading(false);
                    setSnackbarMessage('Image uploaded successfully.');
                    setSnackbarOpen(true);
                });
            }
        );
    };

    const handlePdfUpload = (file) => {
        if (!file) return;

        const storageRef = ref(storage, `pdfs/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        setPdfUploading(true);

        uploadTask.on(
            'state_changed',
            () => { },
            (error) => {
                console.error('Error uploading PDF:', error);
                setSnackbarMessage('Failed to upload PDF.');
                setSnackbarOpen(true);
                setPdfUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setPostState((prevState) => ({ ...prevState, pdfUrl: downloadURL }));
                    setPdfUploading(false);
                    setSnackbarMessage('PDF uploaded successfully.');
                    setSnackbarOpen(true);
                });
            }
        );
    };

    const handleDeletePost = async (id) => {
        try {
            await deleteDocument(id);
            setSnackbarMessage('Document deleted successfully.');
        } catch (error) {
            console.error('Error deleting document:', error);
            setSnackbarMessage('Failed to delete document.');
        }
    };

    return (
        <Box sx={{ p: 4, py: "10vh" }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
                Types Of SubDepartment
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select SubDepartment</InputLabel>
                <Select
                    value={currentSubDepartment || ''}
                    onChange={(event) => {
                        setCurrentSubDepartment(event.target.value);
                        setPostState((prevState) => ({ ...prevState, subDepartmentId: event.target.value }));
                    }}
                    label="Select SubDepartment"
                    displayEmpty
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    sx={{ borderColor: 'gray', '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey' } }}
                >
                    {/* {isLoadingSubDepartments && <MenuItem disabled>Loading...</MenuItem>} */}
                    {filteredSubDepartments?.map(subDepartment => (
                        <MenuItem key={subDepartment.id} value={subDepartment.id}>
                            {subDepartment.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Button
                variant="contained"
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ mb: 3 }}
            >
                Add New Document
            </Button>

            <TableContainer component={Paper} elevation={8} sx={{ borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#3f51b5', color: '#fff' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'black' }}><strong>Title</strong></TableCell>
                            <TableCell sx={{ color: 'black' }}><strong>Description</strong></TableCell>
                            <TableCell sx={{ color: 'black' }}><strong>Image</strong></TableCell>
                            <TableCell sx={{ color: 'black' }}><strong>PDF</strong></TableCell>
                            <TableCell sx={{ color: 'black' }}><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents?.map((post) => (
                            <Fade key={post.id} in={true}>
                                <TableRow>
                                    <TableCell>{post.title}</TableCell>
                                    <TableCell>
                                        {post.description?.length > 50 ? `${post.description.slice(0, 50)}...` : post.description}
                                    </TableCell>
                                    <TableCell>
                                        {post.image && <img src={post.image} alt="Post Thumbnail" style={{ maxWidth: '100px', height: 'auto' }} />}
                                    </TableCell>
                                    <TableCell>
                                        {post.pdfUrl ? (
                                            <a href={post.pdfUrl} target="_blank" rel="noopener noreferrer">
                                                View PDF
                                            </a>
                                        ) : (
                                            <span>No PDF available</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit">
                                            <IconButton onClick={() => handleOpenDialog(post)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton color="error" onClick={() => handleDeletePost(post.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            </Fade>
                        ))}
                    </TableBody>

                </Table>
            </TableContainer>
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '1.25rem'
                    }}
                >
                    {postState.id ? 'Edit Post' : 'Add New Post'}
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: '20px',
                        backgroundColor: '#f5f5f5'
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Select SubDepartment</InputLabel>
                            <Select
                                value={currentSubDepartment || ''}
                                onChange={(event) => {
                                    setCurrentSubDepartment(event.target.value);
                                    setPostState((prevState) => ({ ...prevState, subDepartmentId: event.target.value }));
                                }}
                                label="Select SubDepartment"
                            >
                                {isLoadingSubDepartments && <MenuItem disabled>Loading...</MenuItem>}
                                {subDepartments.map(subDepartment => (
                                    <MenuItem key={subDepartment.id} value={subDepartment.id}>
                                        {subDepartment.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField
                        fullWidth
                        label="Title"
                        variant="outlined"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }}
                        value={postState.title}
                        onChange={(e) => setPostState({ ...postState, title: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label="Write the post description..."
                        variant="outlined"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                height: "20vh"
                            }
                        }}
                        value={postState.description}
                        onChange={(e) => setPostState({ ...postState, description: e.target.value })}
                    />


                    <TextField
                        fullWidth
                        label="Author"
                        variant="outlined"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px'
                            }
                        }}
                        value={postState.author}
                        onChange={(e) => setPostState({ ...postState, author: e.target.value })}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                            variant="contained"
                            component="label"
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#115293'
                                }
                            }}
                        >
                            Upload Image
                            <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
                        </Button>
                        {imageUploading ? (
                            <CircularProgress />
                        ) : (
                            postState.image && <img src={postState.image} alt="Uploaded" style={{ width: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
                        )}
                        <Button
                            variant="contained"
                            component="label"
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#115293'
                                }
                            }}
                        >
                            Upload PDF
                            <input hidden accept="application/pdf" type="file" onChange={handlePdfUpload} />

                        </Button>
                        {pdfUploading ? (
                            <CircularProgress />
                        ) : (
                            postState.pdfUrl && (
                                <a href={postState.pdfUrl} target="_blank" rel="noopener noreferrer">
                                    View PDF
                                </a>
                            )
                        )},
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: '16px',
                        backgroundColor: '#f5f5f5',
                        borderTop: '1px solid #ddd'
                    }}
                >
                    <Button onClick={handleCloseDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleSavePost} color="primary" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                <Alert onClose={handleCloseSnackbar} severity={mutation.isError ? 'error' : 'success'}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TypesManagement;
